/**
 * ReservationDetailPanel — the side Sheet showing a single reservation lead.
 *
 * Renders guest/stay fields (with an "Ask guest" indicator for missing data),
 * the backend-derived suggested next action, the missing-fields list, the
 * valid-only status transition control, and the offer draft surface.
 *
 * The status transition is NON-OPTIMISTIC: the UI awaits the backend, surfaces
 * loading, and handles a 409 (illegal transition / concurrent update) by
 * keeping the current state and showing the conflict.
 */

import { useTransitionReservation } from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  StatusBadge,
  toast,
} from '@hostesa/ui';
import { useEffect, useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import type { Reservation, ReservationStatus } from './_deps';
import { asString, isConflict } from './_format';
import { namespace } from './i18n';
import { OfferDraftCard } from './offer-draft-card';
import { OFFER_STAGES, reservationStatusVariant, validTransitions } from './transitions';

interface FieldRowProps {
  label: string;
  value: string | undefined;
  fallback: string;
  testId: string;
}

function FieldRow({ label, value, fallback, testId }: FieldRowProps) {
  const present = value !== undefined && value.length > 0;
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={testId}>
      <dt className="text-sm text-[var(--color-stone-mid)]">{label}</dt>
      <dd
        className={
          present
            ? 'text-sm text-[var(--color-charcoal)] text-right'
            : 'text-sm italic text-[var(--color-warm-stone-deep)] text-right'
        }
      >
        {present ? value : fallback}
      </dd>
    </div>
  );
}

export interface ReservationDetailPanelProps {
  reservation: Reservation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReservationDetailPanel({
  reservation,
  open,
  onOpenChange,
}: ReservationDetailPanelProps) {
  const { t } = useT(namespace);
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDate } = useFormatters(timezone);
  const transition = useTransitionReservation(tenantId, hotelId);

  const [target, setTarget] = useState<ReservationStatus | ''>('');

  // Reset the chosen transition whenever the open reservation changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reservation?.id is a deliberate trigger — reset must re-run when a different reservation opens.
  useEffect(() => {
    setTarget('');
  }, [reservation?.id]);

  if (!reservation) return null;

  const nextStatuses = validTransitions(reservation.status);
  const showOffer = OFFER_STAGES.includes(reservation.status);

  async function applyTransition() {
    if (!reservation || target === '') return;
    try {
      await transition.mutateAsync({
        reservationId: reservation.id,
        body: { status: target },
      });
      setTarget('');
      toast.success(t('transition.success'));
    } catch (err) {
      toast.error(isConflict(err) ? t('transition.conflict') : t('transition.error'));
    }
  }

  const arrival = asString(reservation.arrivalDate);
  const departure = asString(reservation.departureDate);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="reservation-detail-panel"
        className="w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{t('detail.title')}</SheetTitle>
          <div className="pt-1">
            <StatusBadge
              data-testid="reservation-detail-status"
              status={reservationStatusVariant(reservation.status)}
              label={t(`status.${reservation.status}`)}
            />
          </div>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-6 pb-8">
          {/* Suggested next action */}
          <Card data-testid="reservation-next-action">
            <CardHeader>
              <CardTitle>{t('detail.suggestedNextAction')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[var(--color-charcoal)]">
                {asString(reservation.suggestedNextAction) ?? t('detail.noSuggestion')}
              </p>
            </CardContent>
          </Card>

          {/* Guest & stay */}
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.guestStay')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <FieldRow
                  testId="reservation-field-arrival"
                  label={t('detail.arrival')}
                  value={arrival ? formatDate(arrival) : undefined}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-departure"
                  label={t('detail.departure')}
                  value={departure ? formatDate(departure) : undefined}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-adults"
                  label={t('detail.adults')}
                  value={asString(reservation.adults)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-children"
                  label={t('detail.children')}
                  value={asString(reservation.children)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-room"
                  label={t('detail.roomPreference')}
                  value={asString(reservation.roomTypePreference)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-board"
                  label={t('detail.boardPreference')}
                  value={asString(reservation.boardPreference)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="reservation-field-language"
                  label={t('detail.contactLanguage')}
                  value={asString(reservation.contactLanguage)}
                  fallback={t('detail.notProvided')}
                />
              </dl>
            </CardContent>
          </Card>

          {/* Missing fields */}
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.missingFields')}</CardTitle>
            </CardHeader>
            <CardContent>
              {reservation.missingFields.length > 0 ? (
                <ul className="flex flex-col gap-1" data-testid="reservation-missing-fields">
                  {reservation.missingFields.map((field) => (
                    <li
                      key={field}
                      data-testid={`reservation-missing-field-${field}`}
                      className="text-sm text-[var(--color-warm-stone-deep)]"
                    >
                      {field}
                    </li>
                  ))}
                </ul>
              ) : (
                <p
                  className="text-sm text-[var(--color-stone-mid)]"
                  data-testid="reservation-no-missing-fields"
                >
                  {t('detail.noMissingFields')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Offer draft surface */}
          {showOffer && (
            <OfferDraftCard tenantId={tenantId} hotelId={hotelId} reservation={reservation} />
          )}

          {/* Status transition — valid moves only, non-optimistic */}
          <Card data-testid="reservation-transition-card">
            <CardHeader>
              <CardTitle>{t('transition.label')}</CardTitle>
            </CardHeader>
            <CardContent>
              {nextStatuses.length === 0 ? (
                <p
                  className="text-sm text-[var(--color-stone-mid)]"
                  data-testid="reservation-no-transitions"
                >
                  {t('transition.none')}
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={target} onValueChange={(v) => setTarget(v as ReservationStatus)}>
                    <SelectTrigger data-testid="reservation-transition-select" className="w-full">
                      <SelectValue placeholder={t('transition.placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {nextStatuses.map((status) => (
                        <SelectItem
                          key={status}
                          value={status}
                          data-testid={`reservation-transition-option-${status}`}
                        >
                          {t(`status.${status}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="reservation-transition-apply-btn"
                    size="sm"
                    disabled={target === '' || transition.isPending}
                    onClick={() => void applyTransition()}
                  >
                    {transition.isPending ? t('transition.applying') : t('transition.apply')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
